import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Server, ScrollText, Siren, CalendarClock,
  GitCommitHorizontal, Database, HardDriveDownload, BellRing,
  Users as UsersIcon, Settings as SettingsIcon, ShieldCheck, LogOut, Bell, Globe,
  Activity, LifeBuoy, Wallet, Flag, Webhook, Church, Ticket, Bot, Megaphone, MessageCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '@/lib/brand';
import { useAuth, hasRole } from '@/lib/auth';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import NotificationsBell from '@/components/NotificationsBell';

const OPS_NAV = [
  { to: '/app-monitor', icon: Activity, key: 'nav_app_monitor' },
  { to: '/app-support', icon: LifeBuoy, key: 'nav_app_support' },
  { to: '/app-tickets', icon: Ticket, key: 'nav_app_tickets' },
  { to: '/ai-agent', icon: Bot, key: 'nav_ai_agent' },
  { to: '/campaigns', icon: Megaphone, key: 'nav_campaigns' },
  { to: '/whatsapp', icon: MessageCircle, key: 'nav_whatsapp' },
  { to: '/app-churches', icon: Church, key: 'nav_app_churches', roles: ['admin', 'tech'] },
  { to: '/app-finance', icon: Wallet, key: 'nav_app_finance', roles: ['admin', 'tech'] },
  { to: '/app-flags', icon: Flag, key: 'nav_app_flags', roles: ['admin'] },
  { to: '/app-webhooks', icon: Webhook, key: 'nav_app_webhooks', roles: ['admin'] },
];

const NAV = [
  { to: '/', icon: LayoutDashboard, key: 'nav_overview', exact: true },
  { to: '/services', icon: Server, key: 'nav_services' },
  { to: '/logs', icon: ScrollText, key: 'nav_logs' },
  { to: '/incidents', icon: Siren, key: 'nav_incidents' },
  { to: '/maintenance', icon: CalendarClock, key: 'nav_maintenance' },
  { to: '/deployments', icon: GitCommitHorizontal, key: 'nav_deployments' },
  { to: '/database', icon: Database, key: 'nav_database' },
  { to: '/backups', icon: HardDriveDownload, key: 'nav_backups' },
  { to: '/alerts', icon: BellRing, key: 'nav_alerts' },
  { to: '/users', icon: UsersIcon, key: 'nav_users', adminOnly: true },
  { to: '/audit', icon: ShieldCheck, key: 'nav_audit', adminOnly: true },
  { to: '/settings', icon: SettingsIcon, key: 'nav_settings' },
];

export default function AppShell() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const switchLang = (l) => {
    i18n.changeLanguage(l);
    localStorage.setItem('lang', l);
  };

  const initials = (user?.name || 'U')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className="fixed inset-y-0 left-0 w-[260px] bg-card border-r border-border flex flex-col z-30"
        data-testid="app-sidebar"
      >
        <div className="h-16 px-4 flex items-center gap-3 border-b border-border">
          <img src={BRAND.logoWhite} alt={BRAND.name} className="h-9 w-9 rounded-md object-contain bg-black/20 p-1 flex-shrink-0" />
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-display font-semibold text-sm tracking-wide truncate">
              {BRAND.product} <span className="text-[hsl(var(--primary))]">·</span> {BRAND.name}
            </span>
            <span className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))] truncate">
              Maintenance &amp; Monitor
            </span>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* Grupo: Tu Iglesia App (Ops) */}
          <div className="px-3 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
            {t('group_ops')}
          </div>
          {OPS_NAV.filter((n) => !n.roles || hasRole(user, n.roles)).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-150 ` +
                  (isActive
                    ? 'bg-accent text-foreground shadow-[inset_2px_0_0_hsl(var(--primary))]'
                    : 'text-[hsl(var(--muted-foreground))] hover:text-foreground hover:bg-accent/60')
                }
                data-testid={`sidebar-nav-item-${item.to.replace('/', '')}`}
              >
                <Icon className="h-4 w-4" />
                <span>{t(item.key)}</span>
              </NavLink>
            );
          })}

          {/* Grupo: BOMM interno */}
          <div className="px-3 pt-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">
            {t('group_bom')}
          </div>
          {NAV.filter((n) => !n.adminOnly || hasRole(user, ['admin'])).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-150 ` +
                  (isActive
                    ? 'bg-accent text-foreground shadow-[inset_2px_0_0_hsl(var(--primary))]'
                    : 'text-[hsl(var(--muted-foreground))] hover:text-foreground hover:bg-accent/60')
                }
                data-testid={`sidebar-nav-item-${item.to.replace('/', '') || 'overview'}`}
              >
                <Icon className="h-4 w-4" />
                <span>{t(item.key)}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] px-1 mb-2">
            {BRAND.tagline_es}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="pl-[260px]">
        {/* Topbar */}
        <header
          className="sticky top-0 z-20 h-16 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border"
          data-testid="app-topbar"
        >
          <div className="px-6 h-full flex items-center gap-3">
            <div className="text-sm text-[hsl(var(--muted-foreground))]">
              <span className="hidden md:inline">{BRAND.product} • {BRAND.name}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {/* Language toggle */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="language-toggle" className="gap-2">
                    <Globe className="h-4 w-4" />
                    <span className="text-xs uppercase">{i18n.language}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => switchLang('es')} data-testid="language-option-es">
                    Español
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => switchLang('en')} data-testid="language-option-en">
                    English
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <NotificationsBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 pl-2" data-testid="profile-menu-button">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-[hsl(var(--primary)/.15)] text-[hsl(var(--primary))] text-xs font-semibold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left hidden md:block">
                      <div className="text-xs font-semibold leading-tight">{user?.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                        {user?.role}
                      </div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm">{user?.name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="profile-settings">
                    <SettingsIcon className="h-4 w-4 mr-2" /> {t('nav_settings')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="profile-logout">
                    <LogOut className="h-4 w-4 mr-2" /> {t('sign_out')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
